import type { ApiRoomPollJSON } from 'mastodon/api_types/rooms';

// Mirrors RoomPoll::MIN_OPTIONS / MAX_OPTIONS / MAX_QUESTION / MAX_OPTION.
// The server is the enforcement; these keep the composer from offering a poll
// it already knows will be refused.
export const MIN_POLL_OPTIONS = 2;
export const MAX_POLL_OPTIONS = 6;
export const MAX_POLL_QUESTION = 200;
export const MAX_POLL_OPTION = 80;

export type VoteCommand =
  | { kind: 'none' }
  | { kind: 'vote'; optionIndex: number }
  | { kind: 'error'; message: string };

/**
 * Voting by typing, which ticket 29 asks for explicitly ("Users can vote by
 * typing: 1 or /vote 1").
 *
 * Two different rules, on purpose:
 *
 * `/vote 3` is always treated as a vote while a poll is open. It is unambiguous
 * — nobody types that by accident — so a bad number gets an error rather than
 * being posted to chat as if it were conversation.
 *
 * A BARE `3` is treated as a vote only while a poll is open AND the sender has
 * not voted yet. Otherwise someone counting "3, 2, 1" into a live room would
 * have their words silently swallowed as votes. Once you have voted, digits are
 * just chat again.
 *
 * Option numbers are 1-based for the person typing and 0-based on the wire.
 */
export const parseVoteCommand = (
  text: string,
  poll: ApiRoomPollJSON | null | undefined,
): VoteCommand => {
  const trimmed = text.trim();
  if (trimmed === '') return { kind: 'none' };

  const explicit = /^\/vote\s+(\d{1,2})$/i.exec(trimmed);
  const bare = /^(\d{1,2})$/.exec(trimmed);

  if (!explicit && !bare) return { kind: 'none' };

  // A slash command is never chat, so it gets a real answer even with no poll
  // running. A bare digit with no poll running is just someone saying "3".
  if (!poll || poll.closed) {
    if (!explicit) return { kind: 'none' };

    return {
      kind: 'error',
      message: poll?.closed
        ? 'This poll has ended.'
        : 'There is no poll running.',
    };
  }

  if (!explicit && poll.own_vote !== null) return { kind: 'none' };

  const number = Number((explicit ?? bare)?.[1]);
  const optionIndex = number - 1;

  if (!(optionIndex >= 0 && optionIndex < poll.options.length)) {
    if (!explicit) return { kind: 'none' };

    return {
      kind: 'error',
      message: `Pick a number between 1 and ${poll.options.length}.`,
    };
  }

  if (poll.own_vote !== null) {
    return { kind: 'error', message: 'You have already voted in this poll.' };
  }

  return { kind: 'vote', optionIndex };
};
